import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const tenantId = searchParams.get('tenantId');
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Step 1: Verify the invitation token
  useEffect(() => {
    async function verifyToken() {
      try {
        setLoading(true);
        console.log("Verifying token:", token);
        const response = await fetch(`${API_BASE_URL}/api/invitations/${token}/verify`);
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Invalid invitation');
        }
        
        console.log("Invitation verified:", data);
        setInvitation(data);
      } catch (err) {
        console.error("Error verifying token:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (token) {
      verifyToken();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  // Step 2: Once verified, check if user is logged in
  useEffect(() => {
    if (!loading && invitation && !isAuthenticated) {
      console.log("Invitation token before redirect:", token);
      
      // Store token in sessionStorage for secure token storage
      console.log("Storing invitation token in sessionStorage:", token);
      sessionStorage.setItem('invitation_token', token);
      
      // Also store with timestamp for debugging
      const tokenData = {
        token: token,
        timestamp: Date.now(),
        tenantId: tenantId
      };
      sessionStorage.setItem('invitation_data', JSON.stringify(tokenData));
      
      // Use Auth0's proper authorizationParams format
      // Log parameters being passed to loginWithRedirect
      console.log("Parameters for loginWithRedirect:", {
        state: JSON.stringify({ invitation_token: token, tenant_id: tenantId }),
        appState: {
          invitation_token: token,
          tenant_id: tenantId,
          returnTo: '/callback'
        }
      });

      // Update state parameter to use plain JSON string
      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
          // Use state parameter (Auth0's official way to preserve data)
          state: JSON.stringify({ invitation_token: token, tenant_id: tenantId })
        },
        // Use appState as backup
        appState: { 
          invitation_token: token,
          tenant_id: tenantId,
          returnTo: '/callback'
        }
      }).catch((error) => {
        console.error("Error during loginWithRedirect:", error);
      });
    }
  }, [invitation, loading, isAuthenticated, loginWithRedirect, token, tenantId]);

  // No need for Step 3 (accepting invitation) - it's now handled in AcceptInviteComplete

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white shadow-lg rounded-lg max-w-md w-full">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          <p className="text-center mt-4 text-gray-600">Processing your invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white shadow-lg rounded-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Invitation Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="p-8 bg-white shadow-lg rounded-lg max-w-md w-full">
        {invitation && isAuthenticated ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-center mt-4 text-gray-600">You're already logged in. Processing...</p>
          </div>
        ) : invitation && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Accept Invitation</h1>
            <p className="mb-2">
              You've been invited to join <strong>{invitation.organizationName}</strong> as a <strong>{invitation.role}</strong>.
            </p>
            <p className="text-gray-500">Please wait while we redirect you to log in...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;